import {
  addTemplateGroup,
  addTemplateLineItem,
  addTemplateResolutionOption,
  addTemplateTask,
  deleteTemplateGroup,
  deleteTemplateLineItem,
  deleteTemplateResolutionOption,
  deleteTemplateTask,
  ScanTemplateGroup,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplateRoot,
  ScanTemplateTask,
  updateScanTemplatePart,
  UpdateScanTemplatePartWithContext,
  updateScanTemplateRoot,
  updateTemplateGroup,
  updateTemplateLineItem,
  updateTemplateResolutionOption,
  updateTemplateTask,
} from '@kaiizen/kai-scan-template-system';
import { useMemo } from 'react';

export type ScanTemplateDataMutators = {
  updateTemplate: (updates: Partial<ScanTemplateRoot>) => void;

  addGroup: (group: ScanTemplateGroup, index?: number) => void;
  updateGroup: (groupId: string, updates: Partial<ScanTemplateGroup>) => void;
  deleteGroup: (groupId: string) => void;

  addTask: (groupId: string | undefined, task: ScanTemplateTask, index?: number) => ScanTemplateTask;
  updateTask: (groupId: string | undefined, taskId: string, updates: Partial<ScanTemplateTask>) => void;
  removeTask: (groupId: string | undefined, taskId: string) => void;

  addResolutionOption: (groupId: string | undefined, taskId: string, resolutionOption: ScanTemplateOption, index?: number) => ScanTemplateOption;
  updateResolutionOption: (groupId: string | undefined, taskId: string, resolutionOptionId: string, updates: Partial<ScanTemplateOption>) => void;
  deleteResolutionOption: (groupId: string | undefined, taskId: string, resolutionOptionId: string) => void;

  addLineItem: (
    groupId: string | undefined,
    taskId: string,
    resolutionOptionId: string,
    lineItem: ScanTemplateLineItem,
    index?: number,
  ) => ScanTemplateLineItem;
  updateLineItem: (groupId: string | undefined, taskId: string, resolutionOptionId: string, lineItemId: string, updates: Partial<ScanTemplateLineItem>) => void;
  deleteLineItem: (groupId: string | undefined, taskId: string, resolutionOptionId: string, lineItemId: string) => void;

  updatePart: (updatesWithContext: UpdateScanTemplatePartWithContext) => void;
};

export const useScanTemplateDataMutators = (
  setSowTemplate: (templateUpdater: (st: ScanTemplateRoot) => ScanTemplateRoot) => void,
) => {
  const scanTemplateMutators = useMemo((): ScanTemplateDataMutators => ({
    updateTemplate: updates => setSowTemplate(s => updateScanTemplateRoot(s, updates)),

    addGroup: (group, index = 0) => setSowTemplate(s => addTemplateGroup(s, group, index)),
    updateGroup: (groupId, updates) => setSowTemplate(s => updateTemplateGroup(s, groupId, updates)),
    deleteGroup: groupId => setSowTemplate(s => deleteTemplateGroup(s, groupId)),

    addTask: (groupId, task, index = 0) => {
      setSowTemplate(s => addTemplateTask(s, groupId, task, index));
      return task;
    },
    updateTask: (groupId, taskId, updates) => setSowTemplate(s => updateTemplateTask(s, groupId, taskId, updates)),
    removeTask: (groupId, taskId) => setSowTemplate(s => deleteTemplateTask(s, groupId, taskId)),

    addResolutionOption: (groupId, taskId, resolutionOption, index = 0) => {
      setSowTemplate(s => addTemplateResolutionOption(s, groupId, taskId, resolutionOption, index));
      return resolutionOption;
    },
    updateResolutionOption: (groupId, taskId, resolutionOptionId, updates) =>
      setSowTemplate(s => updateTemplateResolutionOption(s, groupId, taskId, resolutionOptionId, updates)),
    deleteResolutionOption: (groupId, taskId, resolutionOptionId) =>
      setSowTemplate(s => deleteTemplateResolutionOption(s, groupId, taskId, resolutionOptionId)),

    addLineItem: (groupId, taskId, resolutionOptionId, lineItem, index = 0) => {
      setSowTemplate(s => addTemplateLineItem(s, groupId, taskId, resolutionOptionId, lineItem, index));
      return lineItem;
    },
    updateLineItem: (groupId, taskId, resolutionOptionId, lineItemId, updates) =>
      setSowTemplate(s => updateTemplateLineItem(s, groupId, taskId, resolutionOptionId, lineItemId, updates)),
    deleteLineItem: (groupId, taskId, resolutionOptionId, lineItemId) =>
      setSowTemplate(s => deleteTemplateLineItem(s, groupId, taskId, resolutionOptionId, lineItemId)),
    updatePart: updatesWithContext => setSowTemplate(s => updateScanTemplatePart(s, updatesWithContext)),
  }), []); // empty dependency array ensures the object is created only once
  return scanTemplateMutators;
};
